import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { ProjectContext } from '@jira/router-resources__project-context/src/ProjectContext.types.tsx';
import { getClient } from '@jira/platform__graphql-multiple-clients/src/main.tsx';
import { GIRA_APOLLO_CLIENT } from '@jira/platform__graphql-multiple-clients/src/common/constants.tsx';
import { getGiraClient } from '../../controllers/use-issue-create-from-anywhere-context/index.tsx';
import { getIssueCreateProjectForMount } from '../../ui/modal/create-form/getIssueCreateProjectForMount.tsx';
import { getQueryVariables } from '../../ui/modal/create-form/getQueryVariables.tsx';
import type { QueryVariables } from '../../common/types/index.tsx';
import type {
	CreateConfigQuery,
	CreateSubtaskConfigQuery,
	CreateConfigQueryInContext,
} from '../../common/types/create-config-query.tsx';
import { getConsolidatedCreateConfigQuery } from '../fetch-consolidated-create-config-query/getConsolidatedCreateConfigQuery.tsx';

type StorageVariables = {
	cloudId: string;
	applicationKey: 'JIRA_SOFTWARE';
	shouldCheckForStorageLimit: boolean;
};

export type PreloadModalLoadQueryArgs = {
	projectContext: ProjectContext | null;
	storageVariables: StorageVariables;
	withMachineLearning: boolean;
	originUrl?: string;
};

export const preloadModalLoadQuery = ({
	projectContext,
	storageVariables,
	withMachineLearning,
	originUrl,
}: PreloadModalLoadQueryArgs): void => {
	const query = getConsolidatedCreateConfigQuery({
		isInContext: false,
		isSubtask: false,
		withML: withMachineLearning,
		isCreateLinkedIssue: false,
		isCreateLinkedIssueFromIssueViewFGOn: !!fg('create_linked_issues_trigger_point_issue_view'),
		evaluateMiniMode: true,
		isModernised: true,
	});

	const variables: QueryVariables = {
		...getQueryVariables(getIssueCreateProjectForMount(projectContext), null),
		issueTypes: null,
		requestTypes: null,
		additionalContext: null,
		defaultValues: null,
		parentId: null,
		...storageVariables,
		evaluateMiniMode: true,
	};

	const client = getClient({
		clientId: GIRA_APOLLO_CLIENT,
		clientCreator: () => getGiraClient().clientCreator(originUrl),
	});

	client
		.query<
			CreateConfigQuery & CreateSubtaskConfigQuery & CreateConfigQueryInContext,
			QueryVariables
		>({
			query,
			variables,
			fetchPolicy: 'network-only',
			errorPolicy: 'all',
		})
		.catch((error) => {
			log.safeWarnWithoutCustomerData(
				'issue-create-modal.services.modal-load-query.preload-modal-load-query',
				'Best-effort modal-load query preload failed; click-time fetch will retry',
				error instanceof Error ? error : new Error(String(error)),
			);
		});
};
